/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import _ from 'lodash';
import { Bar } from 'react-chartjs-2';
import { Network } from 'vis-network';
import { NodeArrWrapper } from 'relevant-shared/booster/utils';
import { byIdMap } from 'relevant-shared/objects/objectStore';
import COUNTRIES from 'relevant-shared/reportData/countries';
import { stores } from '../../stores';
import classes from '../../api/classes';

const { Publisher } = classes;

const codeToPlacMap = async (codes) => {
	await stores.publishers.loadAll();
	const { publishers } = stores.publishers;
	const allSites = _.flatMap(publishers, 'websites');
	const byId = {
		..._.keyBy(publishers, 'id'),
		..._.keyBy(allSites, 'id'),
		..._(allSites).flatMap('placements').keyBy('id').value(),
	};
	const ids = await Publisher.adUnitCodesToPlacementIds({ codes });
	return _.mapValues(_.zipObject(codes, ids), (id, code) => byId[id] || byId[code]);
};

const maybeToReadable = (name, feature) => {
	if (feature.type === 'country' && typeof name === 'number') {
		// eslint-disable-next-line no-bitwise
		return COUNTRIES[String.fromCodePoint(name & 0xFF, name >> 8)];
	}
	if (feature.type !== 'standard' || !name) {
		return name;
	}
	if (feature.name === 'publisherId') {
		return byIdMap('Publisher')[name]?.name || name;
	}
	if (feature.name === 'siteId') {
		return byIdMap('Site')[name]?.domain || name;
	}
	if (feature.name === 'placementId') {
		const [fst, ...rest] = name.split('-');
		if (!rest.length) {
			return name;
		}
		const remaining = rest.join('-');
		const site = byIdMap('Site')[fst];
		const plac = byIdMap('Placement')[remaining.slice(0, 24)];
		if (site && plac) {
			const last = remaining.slice(24);
			return `${site.domain} >> ${plac.name}${last ? ` >> ${last}` : ''}`;
		} else if (site) {
			return `${site.domain} >> ${remaining}`;
		}
		return byIdMap('Site')[name]?.domain || name;
	}
	return name;
};

const modelDesc = (model) => `${model.label} [${model.modelNr}]`;

const featureName = (feature) => {
	let { name } = feature;
	if (!name) {
		name = feature.type;
	}
	if (feature.type === 'calc') {
		name = ['name', 'metric', 'msRange', ...(feature.divideByNrBack
			? ['calcType', 'divideByNrBack', 'slotsBackJump']
			: []
		)].map((f) => feature[f]).join(':');
	}
	return name || '[Unknown]';
};

const featureDesc = (feature) => ['featureIdx', 'type', 'name']
	.map((f) => feature[f])
	.filter((v) => ![null, undefined, ''].includes(v))
	.join(' - ');

class NodeWrapper {
	constructor(params) {
		Object.assign(this, params);
	}

	get feature() {
		return this.isLeaf ? null : this.model.features[this.featureIdx];
	}

	getLabel() {
		const { value, feature, isLeaf, featureIdx, tree } = this;
		const numStr = (n) => {
			const str = n.toFixed(3);
			return parseInt(str, 10) === n ? n.toString() : str;
		};
		if (isLeaf) {
			return numStr(value);
		}
		const { order, binLimits } = tree.featureMap[featureIdx] || {};
		if (!order) {
			const binVal = feature.binLimits[value - 1];
			return `${featureName(feature)} < ${numStr(binVal)}`;
		}
		const maybeName = maybeToReadable(binLimits[value - 1]?.name, feature);
		return `${featureName(feature)} < ${maybeName ?? '[INVALID]'} (${value - 1})`;
	}
}

class TreeWrapper {
	constructor({ tree, model, trees, useCollectionValMap }) {
		Object.assign(this, { ...tree, model, featureMap: {}, nodes: [], edges: [] });
		this.treeNodes.forEach((arr, idx) => {
			const node = new NodeWrapper({
				tree: this,
				model,
				...new NodeArrWrapper(arr).plain(),
				id: `idx_${idx}`,
			});
			this.nodes.push(node);
			if (node.parentIdx >= 0) {
				const parent = this.nodes[node.parentIdx];
				parent[parent.left ? 'right' : 'left'] = node;
				this.edges.push({ from: parent.id, to: node.id });
				node.parent = parent;
			}
		});
		[this.root] = this.nodes;
		const { valMapping } = (useCollectionValMap && trees[tree.treeIdx - 1]) || this;
		valMapping.forEach(([featureIdx, valIdxToOrderIdx, binLimits]) => {
			const order = [];
			valIdxToOrderIdx.forEach((orderIdx, orgIdx) => {
				const [name, count] = model.features[featureIdx].vals[orgIdx];
				order[orderIdx] = { orgIdx, orderIdx, name, count };
			});
			this.featureMap[featureIdx] = {
				order,
				binLimits: binLimits.map((orderIdx) => order[orderIdx]),
			};
		});
		this.featureValMapping = _.entries(this.featureMap).map(([featureIdx, obj]) => ({
			...obj,
			feature: model.features[featureIdx],
		}));
		this.nodes.forEach((node) => {
			node.label = node.getLabel();
		});
	}
}

function TreeGraph({ tree }) {
	const divRef = useRef(null);
	const networkRef = useRef(null);

	useEffect(() => {
		networkRef.current = new Network(divRef.current, tree, {
			layout: {
				hierarchical: {
					direction: 'UD',
					sortMethod: 'directed',
				},
			},
		});
		return () => {
			networkRef.current?.destroy();
		};
	}, [tree]);

	return (
		<div
			ref={divRef}
			style={{ width: '100%', height: '600px' }}
		/>
	);
}

function DebugChart({ yValues, label, color = '#2b7cff', height = '250px' }) {
	return (
		<div style={{ height }}>
			<Bar
				data={{
					labels: yValues.map((__, i) => i),
					datasets: [{ backgroundColor: color, data: yValues }],
				}}
				options={{
					responsive: true,
					animation: true,
					maintainAspectRatio: false,
					plugins: {
						tooltip: {
							callbacks: { title: () => '', label },
						},
						legend: { display: false },
					},
					scales: {
						x: {
							ticks: { autoSkip: true, maxTicksLimit: 20 },
						},
						y: { beginAtZero: true },
					},
				}}
			/>
		</div>
	);
}

export {
	codeToPlacMap,
	maybeToReadable,
	modelDesc,
	featureName,
	featureDesc,
	TreeWrapper,
	TreeGraph,
	DebugChart,
};
